import React, {
  useCallback, useRef, useEffect,
} from 'react'
import { connect as connectStaticText } from '../../../../StaticText/connect'
import { StaticTextProvider, useTranslatedText } from '../../../../StaticText/StaticTextContext'
import { usePostHogEvent } from '../../../../PostHog/index'
import { handleFocusNavigation } from '../../../../Modal/utils'
import Button, { TYPES as BUTTON_TYPES } from '../../../../Button/index'
import Close from '../../../../Icon/icons/Close/index'
import ShareAndroid from '../../../../Icon/icons/ShareAndroid/index'
import Link from '../../../../Icon/icons/Link/index'
import GaiaLogo, { TYPE_TEAL } from '../../../../GaiaLogo/index'
import { formatDuration } from '../../../../../theme/web-app/index'
import useCopyFeedback from '../../../../../services/community/shared/useCopyFeedback'
import type { StaticTextMap, EnrichedPlaylistItem } from '../../../../../services/community/types'

interface PlaylistSharePreviewProps {
  visible: boolean
  playlistId: string
  playlistName: string
  description?: string
  items: EnrichedPlaylistItem[]
  onDismiss: () => void
  staticText: StaticTextMap
}

const MAX_THUMBNAIL_CARDS = 3

function PlaylistSharePreviewInner ({
  visible,
  playlistId,
  playlistName,
  description,
  items,
  onDismiss,
}: Omit<PlaylistSharePreviewProps, 'staticText'>) {
  const { copied, handleCopy } = useCopyFeedback()
  const overlayRef = useRef<HTMLDivElement>(null)
  const dialogRef = useRef<HTMLDivElement>(null)

  const shareUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/playlist/shared/${playlistId}`
    : ''

  // A11Y-3: Escape key + focus trap (matches FollowersModal pattern)
  const previousActiveElementRef = useRef<Element | null>(null)

  useEffect(() => {
    if (!visible) return undefined

    previousActiveElementRef.current = document.activeElement

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onDismiss()
        return
      }
      if (dialogRef.current) {
        handleFocusNavigation(e, dialogRef.current)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      if (previousActiveElementRef.current instanceof HTMLElement) {
        previousActiveElementRef.current.focus()
      }
    }
  }, [visible, onDismiss])

  // Focus the dialog when it opens
  useEffect(() => {
    if (visible && dialogRef.current) {
      dialogRef.current.focus()
    }
  }, [visible])

  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === overlayRef.current) {
      onDismiss()
    }
  }, [onDismiss])

  const trackPlaylistShared = usePostHogEvent('community_playlist_shared')

  const handleCopyLink = useCallback(() => {
    handleCopy(shareUrl)
    if (playlistName) trackPlaylistShared(playlistName, 'copy_link')
  }, [handleCopy, shareUrl, trackPlaylistShared, playlistName])

  const handleNativeShare = useCallback(async () => {
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({
          title: playlistName,
          url: shareUrl,
        })
        if (playlistName) trackPlaylistShared(playlistName, 'native_share')
      } catch {
        // User cancelled or share failed — silently ignore (do not track)
      }
    }
  }, [playlistName, shareUrl, trackPlaylistShared])

  const stCopyLink = useTranslatedText({ keys: ['copyLink'] })
  const stCopied = useTranslatedText({ keys: ['copied'] })
  const stShare = useTranslatedText({ keys: ['share'] })
  const stSharePlaylist = useTranslatedText({ keys: ['sharePlaylist'] })
  const stClose = useTranslatedText({ keys: ['close'] })

  if (!visible) return null

  // Filter to items that have a valid thumbnail image
  const thumbnailItems = items
    .filter((item) => !!item.imageUrl)
    .slice(0, MAX_THUMBNAIL_CARDS)
  const copyLinkLabel = copied ? stCopied : stCopyLink
  const shareLabel = stShare
  const hasNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function'

  return (
    <div
      className="playlist-share-preview__overlay"
      ref={overlayRef}
      onClick={handleOverlayClick}
      role="presentation"
    >
      <div
        className="playlist-share-preview"
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={stSharePlaylist}
      >
        <Button
          type={BUTTON_TYPES.LINK}
          className="playlist-share-preview__close"
          onClick={onDismiss}
          aria-label={stClose}
        >
          <Close />
        </Button>

        {/* Logo + Description */}
        <div className="playlist-share-preview__header">
          <GaiaLogo type={TYPE_TEAL} />
          {description ? (
            <p className="playlist-share-preview__description">
              {description}
            </p>
          ) : null}
        </div>

        {/* Card preview with stacked thumbnails */}
        <div className="playlist-share-preview__card">
          <p className="playlist-share-preview__title">
            {playlistName}
          </p>
          {thumbnailItems.length > 0 ? (
            <div
              className="playlist-share-preview__thumbnails"
              style={{
                /* Include the total stacked offset in the grid's
                   size so the card section can center it properly */
                paddingRight: `${(thumbnailItems.length - 1) * 19}px`,
                paddingBottom: `${(thumbnailItems.length - 1) * 18}px`,
              }}
            >
              {thumbnailItems.map((item, index) => {
                const isFrontCard = index === thumbnailItems.length - 1
                const durationLabel = item.duration
                  ? formatDuration(item.duration) as string
                  : null
                const contentType = item.tileData?.contentType || null
                return (
                  <div
                    key={item.id}
                    className="playlist-share-preview__thumb"
                    style={{
                      zIndex: index + 1,
                      transform: `translate(${index * 19}px, ${index * 18}px)`,
                    }}
                  >
                    <div className="playlist-share-preview__thumb-image">
                      <img
                        src={item.imageUrl}
                        alt={item.title}
                        loading="eager"
                      />
                    </div>
                    <div className="playlist-share-preview__thumb-info">
                      {isFrontCard ? (
                        <>
                          <span className="playlist-share-preview__thumb-title">
                            {item.title}
                          </span>
                          {(contentType || durationLabel) ? (
                            <div className="playlist-share-preview__thumb-tags">
                              {contentType ? (
                                <span className="playlist-share-preview__thumb-tag">
                                  {contentType}
                                </span>
                              ) : null}
                              {contentType && durationLabel ? (
                                <span
                                  className="playlist-share-preview__thumb-tag-dot"
                                  aria-hidden="true"
                                />
                              ) : null}
                              {durationLabel ? (
                                <span className="playlist-share-preview__thumb-tag">
                                  {durationLabel}
                                </span>
                              ) : null}
                            </div>
                          ) : null}
                        </>
                      ) : null}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : null}
          </div>

        {/* Share actions */}
        <div className="playlist-share-preview__actions">
          <Button
            type={BUTTON_TYPES.GHOST}
            className="playlist-share-preview__cta-btn"
            onClick={handleCopyLink}
            aria-label={copyLinkLabel}
          >
            <Link />
            <span className="playlist-share-preview__cta-btn-label">
              {copyLinkLabel}
            </span>
          </Button>
          {hasNativeShare ? (
            <Button
              type={BUTTON_TYPES.GHOST}
              className="playlist-share-preview__cta-btn playlist-share-preview__cta-btn--icon-only"
              onClick={handleNativeShare}
              aria-label={shareLabel}
            >
              <ShareAndroid />
              <span className="playlist-share-preview__cta-btn-label">
                {shareLabel}
              </span>
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function PlaylistSharePreview (props: PlaylistSharePreviewProps) {
  return (
    <StaticTextProvider staticText={props.staticText}>
      <PlaylistSharePreviewInner {...props} />
    </StaticTextProvider>
  )
}

export { PlaylistSharePreview }

export default connectStaticText({ storeKey: 'playlistSharePreview' })(
  React.memo(PlaylistSharePreview),
)
